import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageTransition from '../components/Motion/PageTransition';

/**
 * Training & Support Page - Training programs and ongoing support services
 * Focused on accurate training and support services provided by Real Value Group
 */
function TrainingSupport() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // Training & Support Services
  const trainingServices = [
    {
      id: 'onboarding-sessions',
      name: 'Onboarding & User Training',
      svgIcon: (
        <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      description: 'Comprehensive onboarding sessions to ensure your team understands how to use the Power BI dashboards and custom solutions we deliver. We guide your users through every feature, helping them navigate reports and interpret insights with confidence.',
      whatWeProvide: [
        'Guided walkthroughs of dashboards and reports',
        'Hands-on training for all user roles',
        'Navigation and feature demonstrations',
        'Best practices for data interpretation',
        'Report filtering and drill-down techniques',
        'Export and sharing capabilities training'
      ],
      benefits: 'Faster adoption, improved user confidence, and immediate productivity gains from day one.'
    },
    {
      id: 'ongoing-support',
      name: 'Continuous Technical Support',
      svgIcon: (
        <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      description: 'Ongoing technical support to keep your solutions running smoothly as your business evolves. We handle dashboard updates, metric refinements, and address new analytical needs as they arise.',
      whatWeProvide: [
        'Regular check-ins and system health reviews',
        'Dashboard updates and enhancements',
        'Metric modifications and KPI adjustments',
        'Data source integration and updates',
        'Performance optimization and troubleshooting',
        'Bug fixes and issue resolution'
      ],
      benefits: 'Peace of mind knowing your analytics solutions grow with your business and expert help is always available.'
    },
    {
      id: 'data-literacy',
      name: 'Data Literacy Development',
      svgIcon: (
        <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      description: 'Strengthen your team\'s data literacy through focused training on interpreting insights, understanding metrics, and making data-driven decisions with confidence.',
      whatWeProvide: [
        'Understanding KPIs and business metrics',
        'Data interpretation best practices',
        'Identifying trends and patterns',
        'Making informed decisions from dashboards',
        'Asking the right analytical questions',
        'Leveraging insights for business impact'
      ],
      benefits: 'Empowered teams who can confidently use data to drive better business outcomes and strategic decisions.'
    },
    {
      id: 'solution-evolution',
      name: 'Solution Evolution & Enhancement',
      svgIcon: (
        <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      description: 'As your business needs change, we ensure your analytics solutions evolve alongside. From adding new metrics to integrating additional data sources, we keep your tools relevant and valuable.',
      whatWeProvide: [
        'New dashboard features and visualizations',
        'Additional data source integration',
        'Expanded reporting capabilities',
        'Custom metric development',
        'Workflow and automation enhancements',
        'Architecture improvements and scaling'
      ],
      benefits: 'Solutions that remain aligned with your business strategy and continue delivering value as you grow.'
    },
    {
      id: 'documentation',
      name: 'Comprehensive Documentation',
      svgIcon: (
        <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      description: 'Complete documentation and user guides for all delivered solutions, ensuring your team has reliable reference materials and can use dashboards independently.',
      whatWeProvide: [
        'User guides with step-by-step instructions',
        'Dashboard navigation documentation',
        'Feature and capability references',
        'Troubleshooting guides and FAQs',
        'Video tutorials (when applicable)',
        'Quick reference cards for common tasks'
      ],
      benefits: 'Self-sufficient teams with readily available resources, reducing dependency and enabling independence.'
    },
    {
      id: 'continuous-improvement',
      name: 'Continuous Improvement Cycle',
      svgIcon: (
        <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      description: 'Regular reviews and feedback sessions to identify optimization opportunities, gather user insights, and implement improvements that enhance the value of your analytics solutions.',
      whatWeProvide: [
        'Scheduled review meetings',
        'User feedback collection and analysis',
        'Performance metrics and usage tracking',
        'Optimization recommendations',
        'New feature ideation sessions',
        'Best practice updates and knowledge sharing'
      ],
      benefits: 'A partnership focused on maximizing ROI and ensuring your analytics solutions deliver increasing value over time.'
    }
  ];

  return (
    <PageTransition>
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <motion.div
            className="max-w-4xl mx-auto text-center mb-16"
            initial="hidden"
            animate="visible"
            variants={headerVariants}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Training & Ongoing Support
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              We provide dedicated training and continuous support to ensure your team fully understands and maximizes the Power BI dashboards, custom applications, and solutions we build for you.
            </p>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              From onboarding sessions to guided walkthroughs, we teach your users how to navigate reports, interpret insights, and use every feature with confidence. Our ongoing support ensures your team stays empowered as your business evolves—whether it's refining dashboards, updating metrics, or addressing new analytical needs.
            </p>
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-600 mb-8 max-w-3xl mx-auto">
              <p className="text-lg text-gray-800 font-medium">
                Equip your team to confidently use the tools we deliver. Improve adoption, strengthen data literacy, and ensure long-term success with solutions that grow alongside your business. Our clients achieve faster onboarding, higher engagement, and a continuous improvement cycle supported by our expert guidance.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-amber-50 rounded-full text-amber-700 font-semibold">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
              Practical, Hands-On Training
            </div>
          </motion.div>

          {/* Training & Support Services Section */}
          <div className="mb-16">
            <motion.div
              className="max-w-7xl mx-auto space-y-8"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              {trainingServices.map((service) => (
                <motion.div
                  key={service.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                  variants={cardVariants}
                  whileHover={{ y: -4 }}
                >
                  <div className="p-8">
                    {/* Service Header */}
                    <div className="flex items-center gap-3 mb-4">
                      {service.svgIcon}
                      <h3 className="text-2xl font-bold text-gray-900">{service.name}</h3>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                      {service.description}
                    </p>

                    {/* What We Provide */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-3">What We Provide</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {service.whatWeProvide.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Benefits */}
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-600">
                      <p className="text-sm font-semibold text-gray-800">
                        <span className="text-blue-700">Key Benefits:</span> {service.benefits}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* CTA Section */}
          <motion.div
            className="max-w-4xl mx-auto text-center mt-16 p-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-4">Ready to Empower Your Team?</h2>
            <p className="text-amber-100 mb-6 text-lg">
              Let's discuss training programs and support packages tailored to your needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-block bg-white text-amber-600 px-8 py-3 rounded-lg font-semibold hover:bg-amber-50 transition-colors shadow-lg"
              >
                Schedule Training
              </Link>
              <Link
                to="/projects"
                className="inline-block bg-amber-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-amber-800 transition-colors border-2 border-white"
              >
                See What We Build
              </Link>
            </div>
          </motion.div>

          {/* Back to Services */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center text-amber-600 hover:text-amber-800 font-semibold transition-colors group"
            >
              <svg
                className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Services
            </Link>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

export default TrainingSupport;
